import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { getTheme, updateTheme, getSiteRepos } from "@/lib/api";

const ThemeEdit: React.FC = () => {
  const { siteSlug, themeId } = useParams<{ siteSlug: string; themeId: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["theme", siteSlug, themeId],
    queryFn: () => getTheme(siteSlug!, themeId!),
    enabled: !!siteSlug && !!themeId,
  });

  // Fetch deployer repos (Vercel projects)
  const { data: githubProjects, isLoading: loadingRepos } = useQuery({
    queryKey: ["site-gitub-repos", siteSlug],
    queryFn: () => getSiteRepos(siteSlug!),
    enabled: !!siteSlug,
  });

  const repoOptions =
    loadingRepos || !githubProjects
      ? []
      : githubProjects.map((project: any) => ({
          value: project[0],
          label: project[1],
        }));

  const mutation = useMutation({
    mutationFn: (formData: any) => updateTheme(siteSlug!, themeId!, formData),
    onSuccess: () => {
      toast.success("Theme updated!");
      queryClient.invalidateQueries(["theme", siteSlug, themeId]);
      navigate(-1);
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to update theme");
    },
  });

  const [form, setForm] = useState({
    name: "",
    repo: "",
    ref: "",
    env: "",
  });

  useEffect(() => {
    if (data) {
      setForm({
        name: data.theme.name || "",
        repo: data.theme.deploy_options?.repo || "",
        ref: data.theme.deploy_options?.ref || "",
        env: data.theme.deploy_options?.env || "",
      });
    }
  }, [data]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      name: form.name,
      //deploy_options: {
        repo: form.repo,
        ref: form.ref,
        env: form.env,
      //},
    });
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-pink-200-">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-xl p-8 rounded-lg shadow bg-pink-200"
        autoComplete="off"
      >
        <div className="mb-4">
          <Label htmlFor="name" className="block mb-1 font-semibold">Name</Label>
          <Input
            id="name"
            name="name"
            value={form.name}
            onChange={handleChange}
            className="w-full"
            required
          />
        </div>
        <div className="mb-2 font-semibold">Vercel options</div>
        <div className="mb-4">
          <Label htmlFor="repo" className="block mb-1">Repo</Label>
          <select
            id="repo"
            name="repo"
            value={form.repo}
            onChange={handleChange}
            className="w-full border border-black rounded px-2 py-2"
            required
            disabled={loadingRepos}
          >
            <option value="">Select repo</option>
            {repoOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <div className="text-xs text-gray-700 mt-1">github repo</div>
        </div>
        <div className="mb-4">
          <Label htmlFor="ref" className="block mb-1">Ref</Label>
          <Input
            id="ref"
            name="ref"
            value={form.ref}
            onChange={handleChange}
            className="w-full"
            required
          />
          <div className="text-xs text-gray-700 mt-1">repo branch or commit</div>
        </div>
        <div className="mb-6">
          <Label htmlFor="env" className="block mb-1">Env</Label>
          <Input
            id="env"
            name="env"
            value={form.env}
            onChange={handleChange}
            className="w-full"
            required
          />
          <div className="text-xs text-gray-700 mt-1">production, staging</div>
        </div>
        <Button
          type="submit"
          className="w-full bg-black text-white hover:bg-gray-900"
          disabled={mutation.isLoading}
        >
          Update Theme
        </Button>
      </form>
    </div>
  );
};

export default ThemeEdit;
